/**
 * 휴대폰 뒤로가기(◀) / popstate 통합 처리
 *
 * 우선순위: 확인 모달 → pushBackLayer(오버레이·탭) → Vue Router → 앱 종료 확인
 *
 * ★ history.pushState 시 Vue Router state(current/position/…)를 반드시 보존할 것.
 *   덮어쓰면 이후 router.push 가 Location.replace('…82undefined/') 로 터진다.
 */
import { onMounted, onUnmounted } from 'vue'
import router from '@/router'
import { authStore } from '@/stores/auth'
import { appConfirm, resolveAppConfirm, useAppConfirmState } from '@/composables/useAppConfirm'

type BackLayer = {
  id: string
  onBack: () => void
}

type RouterHistoryState = {
  back?: string | null
  current?: string
  forward?: string | null
  position?: number
  replaced?: boolean
  scroll?: { left: number; top: number } | null
  caionBackLayer?: string
  caionExitGuard?: boolean
  caionTermsSheet?: boolean
  [key: string]: unknown
}

const layerStack: BackLayer[] = []
let suppressPop = false
let installed = false

const HOME_PATHS = new Set(['/caregiver', '/owner', '/sw'])

const currentPath = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}` || '/'

const isValidHistoryPath = (v: unknown): v is string =>
  typeof v === 'string' && v.startsWith('/') && !v.includes('undefined')

/** Vue Router가 기대하는 history.state 필드를 유지한 채 push/replace */
const pushRouterAwareState = (extra: Record<string, unknown>, replace = false) => {
  const prev = (history.state && typeof history.state === 'object'
    ? (history.state as RouterHistoryState)
    : {}) as RouterHistoryState
  const current = isValidHistoryPath(prev.current) ? prev.current : currentPath()
  const next: RouterHistoryState = {
    ...prev,
    ...extra,
    current,
    back: prev.back ?? null,
    forward: prev.forward ?? null,
    position: typeof prev.position === 'number' ? prev.position : Math.max(0, history.length - 1),
    replaced: replace ? true : !!prev.replaced,
    scroll: prev.scroll ?? null,
  }
  if (replace) history.replaceState(next, '')
  else history.pushState(next, '')
}

/**
 * 셸 탭 pushState 등으로 current 가 날아간 state 복구.
 * openInbox 등 router.push 직전에 호출.
 */
export function repairVueRouterHistoryState() {
  const prev = (history.state && typeof history.state === 'object'
    ? (history.state as RouterHistoryState)
    : null)
  if (prev && isValidHistoryPath(prev.current) && typeof prev.position === 'number') return
  pushRouterAwareState({}, true)
}

/** 오버레이·탭 전환 등 — history에 한 단계 쌓음 */
export function pushBackLayer(id: string, onBack: () => void): () => void {
  const layer: BackLayer = { id, onBack }
  layerStack.push(layer)
  pushRouterAwareState({ caionBackLayer: id })

  return () => dismissBackLayer(id)
}

/** 버튼으로 닫을 때 history와 스택 동기화 */
export function dismissBackLayer(id: string) {
  const idx = layerStack.findIndex((l) => l.id === id)
  if (idx < 0) return
  const isTop = idx === layerStack.length - 1
  layerStack.splice(idx, 1)
  if (isTop && !suppressPop) {
    suppressPop = true
    history.back()
  }
}

/** 다른 페이지로 이동할 때 — history.back 없이 스택만 제거 */
export function dropBackLayer(id: string) {
  const idx = layerStack.findIndex((l) => l.id === id)
  if (idx >= 0) layerStack.splice(idx, 1)
}

/** 셸 탭 레이어 등 — 언마운트·유틸 페이지 진입 시 스택만 정리 (죽은 콜백·먹통 방지) */
export function dropBackLayersByPrefix(prefix: string) {
  for (let i = layerStack.length - 1; i >= 0; i -= 1) {
    if (layerStack[i].id.startsWith(prefix)) layerStack.splice(i, 1)
  }
}

/** 셸을 떠날 때 — 죽은 뒤로가기 콜백이 다음 페이지의 뒤로가기를 먹지 않게 비운다 */
export function clearBackLayers() {
  layerStack.length = 0
}

/**
 * 셸(하단탭)에서 /app/* 유틸 페이지로 이동.
 * 탭 pushState·레이어가 남아 있으면 router.push 가 실패하거나 페이지가 바로 닫힘.
 */
export async function navigateFromShell(
  to: string | { name: string } | { path: string },
  fallbackPath?: string
) {
  // 셸이 사라지면 회계·내정보 레이어 콜백도 죽는다. 남겨 두면 다음 뒤로가기가 그 콜백을 먹고 일정계획으로 떨어진다.
  clearBackLayers()
  repairVueRouterHistoryState()
  const path =
    typeof to === 'string'
      ? to
      : 'path' in to && to.path
        ? to.path
        : fallbackPath || '/'
  try {
    await router.push(to as any)
  } catch (e) {
    console.error('[nav] navigateFromShell failed:', e, to)
    window.location.assign(fallbackPath || path)
  }
}

/**
 * UI 버튼(배경 탭·닫기)으로 오버레이 닫기 — history 동기화, popstate에서 종료 확인 방지
 */
export function closeBackLayer(id: string) {
  dropBackLayer(id)
  const st = history.state as { caionBackLayer?: string } | null
  if (st?.caionBackLayer === id) {
    suppressPop = true
    history.back()
  }
}

/**
 * 다른 라우트로 이동하기 직전 — history.back 없이 레이어·state만 정리.
 * (닫기 → history.back 과 router.push 가 경쟁해 새 페이지가 바로 닫히는 것 방지)
 */
export function releaseBackLayer(id: string) {
  dropBackLayer(id)
  const st = history.state as RouterHistoryState | null
  if (st?.caionBackLayer === id) {
    pushRouterAwareState({ caionBackLayer: undefined }, true)
  }
}

const isConfirmOpen = () => useAppConfirmState().open

const canRouterGoBack = () => {
  const st = history.state as { back?: string | null } | null
  return !!(st && st.back != null && st.back !== '')
}

const isAtShellHome = () => {
  const path = router.currentRoute.value.path
  if (!HOME_PATHS.has(path)) return false
  return layerStack.length === 0
}

const tryExitApp = () => {
  window.open('', '_self')?.close()
  if (typeof (navigator as any).app?.exitApp === 'function') {
    ;(navigator as any).app.exitApp()
  }
}

const promptExitApp = async () => {
  pushRouterAwareState({ caionExitGuard: true })
  const ok = await appConfirm({
    title: '앱 종료',
    message: 'caion 앱을 종료하시겠습니까?',
    confirmText: '종료',
    cancelText: '취소',
  })
  if (ok) {
    tryExitApp()
  }
  const st = history.state as { caionExitGuard?: boolean } | null
  if (st?.caionExitGuard) {
    suppressPop = true
    history.back()
  }
}

const onPopState = async () => {
  // 예전 탭 pushState(legacy) 로 Vue Router 가 current 없는 state 를 동기화했을 수 있음
  queueMicrotask(() => repairVueRouterHistoryState())

  if (suppressPop) {
    suppressPop = false
    return
  }

  if (isConfirmOpen()) {
    resolveAppConfirm(false)
    pushRouterAwareState({ caionExitGuard: true })
    return
  }

  const top = layerStack.pop()
  if (top) {
    top.onBack()
    return
  }

  // 셸 언마운트 후 남은 고아 pushState — 종료 확인으로 화면이 잠기지 않게 무시
  const st = history.state as { caionBackLayer?: string; caionExitGuard?: boolean } | null
  if (st?.caionBackLayer) return

  // Vue Router가 처리한 페이지 이동(/app/support 등) — 추가 동작 없음
  if (canRouterGoBack()) return

  if (isAtShellHome()) {
    await promptExitApp()
  }
}

export function useAppBackNavigation() {
  onMounted(() => {
    if (installed) return
    installed = true
    window.addEventListener('popstate', onPopState)
  })

  onUnmounted(() => {
    // 앱 루트 컴포넌트에서만 사용 — 언마운트 시 리스너 유지
  })
}
